import { CellProps, Column, Row } from "@komune-io/g2-layout";
import { FilterComposableField, useFiltersComposable } from "@komune-io/g2-composable";
import { FilterSelect } from "@komune-io/g2-forms";
import { TFunction } from "i18next";
import {
  PlatformPaginatedTable,
  PlatformSectionFilter,
  TableCellTag,
  TableCellText,
} from "@tracabois/template";
import { firstPage, OffsetPagination, PageQuery, PageResult } from "@tracabois/utils";
import React, { useState, useCallback, useMemo } from "react";
import { productList, Realization } from "../../model/realizationModel";
import { useBuildingTypeAllQuery } from "../../../../referentiels/buildingType/api/BuildingTypeApi";
import { CompletionIcon, FiltersComposable } from "@tracabois/components";
import {
  Stack,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { RealizationStateChip } from "../state/RealizationStateChip";

interface RealizationTableProps {
  page?: PageResult<Realization>;
  isLoading?: boolean;
  actions?: React.ReactNode;
  onPageQueryChange: (page: PageQuery) => void;
  onRowClicked: (row: Row<Realization>) => void;
  onDelete?: (id: string) => Promise<void>;
}

export const RealizationTable = (props: RealizationTableProps) => {
  const {
    page,
    isLoading,
    actions,
    onPageQueryChange,
    onRowClicked,
    onDelete,
  } = props;

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Realization | null>(null);

  interface FilterValues {
    search?: string;
    buildingType?: string;
  }

  const extractFilters = useCallback((values?: FilterValues): Record<string, string | undefined> => {
    const filters: Record<string, string | undefined> = {};
    if (values?.buildingType) filters.buildingType = String(values.buildingType);
    return filters;
  }, []);

  const handleFiltersSubmit = useCallback(
    (values: FilterValues) => {
      onPageQueryChange({
        pagination: firstPage,
        search: values.search || undefined,
        filters: extractFilters(values),
      });
    },
    [onPageQueryChange, extractFilters]
  );

  const { formState, submittedFilters, setAdditionalFilter } = useFiltersComposable<FilterValues>({
    onSubmit: handleFiltersSubmit,
  });

  const onPaginationChange = (pagination: OffsetPagination) => {
    onPageQueryChange({
      pagination: pagination,
      search: submittedFilters?.search,
      filters: extractFilters(submittedFilters),
    });
  };

  const handleDeleteClick = useCallback((e: React.MouseEvent, item: Realization) => {
    e.stopPropagation();
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!onDelete || !itemToDelete) return;
    await onDelete(itemToDelete.id);
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  }, [onDelete, itemToDelete]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  }, []);

  const searchFields = useMemo((): FilterComposableField[] => [
    {
      key: "search",
      name: "search",
      type: "textField",
      params: { textFieldType: "search", placeholder: "Rechercher" },
      mandatory: true,
    },
  ], []);

  const buildingTypeQuery = useBuildingTypeAllQuery();
  const buildingTypeOptions = useMemo(() =>
    (buildingTypeQuery.data?.items ?? []).map((item) => ({ key: item.id, label: item.name })),
  [buildingTypeQuery.data]);

  const buildingTypeNameById = useMemo(() => {
    const map: Record<string, string> = {};
    (buildingTypeQuery.data?.items ?? []).forEach((item) => { map[item.id] = item.name; });
    return map;
  }, [buildingTypeQuery.data]);

  const filterComponent = (
    <PlatformSectionFilter>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
        <FiltersComposable
          formState={formState}
          fields={searchFields}
        />
        <FilterSelect
          label="Type bâtiment"
          value={formState.values.buildingType || ""}
          options={buildingTypeOptions}
          onChangeValue={(value) => setAdditionalFilter("buildingType", value)}
          onRemove={() => setAdditionalFilter("buildingType", "")}
          color="default"
          variant="outlined"
        />
      </Box>
      {actions}
    </PlatformSectionFilter>
  );

  const getColumns = (t: TFunction): Column<Realization>[] => {
    const baseColumns: Column<Realization>[] = [
      {
        Header: " ",
        // @ts-ignore
        Cell: ({ row }: CellProps<Realization>) => (
          <CompletionIcon completion={row.original.indicators?.completion || 0} />
        ),
        className: "iconColumn",
      },
      {
        Header: `${t("realization.name", "Nom Réalisation")}`,
        // @ts-ignore
        Cell: ({ row }: CellProps<Realization>) => (
          <TableCellText main={row.original.name} second={row.original.commune || ""} />
        ),
      },
      {
        Header: `${t("realization.status", "Status")}`,
        // @ts-ignore
        Cell: ({ row }: CellProps<Realization>) => {
          return (
            <TableCellTag
              main={<RealizationStateChip item={row.original} editable={false} />}
              second={`Créé le ${new Date(row.original.creationDate).toLocaleDateString("fr-FR")}`}
            />
          );
        },
      },
    ];

    baseColumns.push(
      {
        Header: `${t("realization.buildingType", "Type")}`,
        // @ts-ignore
        Cell: ({ row }: CellProps<Realization>) => {
          const typeId = row.original.buildingType;
          const typeName = typeId ? (buildingTypeNameById[typeId] || typeId) : undefined;
          return (
            <TableCellText
              main={typeName || "-"}
              second={row.original.deliveryYear ? `${row.original.deliveryYear}` : ""}
            />
          );
        },
      },
    );

    baseColumns.push(
      {
        Header: `${t("realization.acteurs", "Produits")}`,
        // @ts-ignore
        Cell: ({ row }: CellProps<Realization>) => <TableCellText main={productList(row.original)} />,
      },
      {
        Header: `${t("realization.volumes", "Volumes")}`,
        // @ts-ignore
        Cell: ({ row }: CellProps<Realization>) => (
          <TableCellText main={row.original.indicators?.wood?.toString() || "0"} second={"m3"} />
        ),
      }
    );

    // Actions column
    if (onDelete) {
      baseColumns.push({
        Header: "Actions",
        // @ts-ignore
        Cell: ({ row }: CellProps<Realization>) => (
          <Box sx={{ display: "flex", gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
            {onDelete && (
              <Tooltip title="Supprimer">
                <IconButton
                  size="small"
                  onClick={(e) => handleDeleteClick(e, row.original)}
                  color="error"
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
        className: "actionsColumn",
      });
    }

    return baseColumns;
  };

  return (
    <Stack
      sx={{
        "& .iconColumn": {
          width: "30px",
        },
        "& .actionsColumn": {
          width: "100px",
        },
      }}
    >
      <PlatformPaginatedTable<Realization>
        page={page}
        columnsDefinition={getColumns}
        isLoading={isLoading}
        actions={filterComponent}
        onPageChange={onPaginationChange}
        onRowClicked={onRowClicked}
      />

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer la réalisation "{itemToDelete?.name}" ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Annuler</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
